import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import { 
  DollarSign, TrendingUp, AlertTriangle, FileText, 
  PieChart, BarChart3, CheckCircle, Clock, ArrowRight,
  Calculator, Target, Wallet, Activity
} from 'lucide-react';

const PlanningDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());


  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await psAPI.getPlanningDashboard(fiscalYear);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching planning dashboard:', error);
      toast.error('Failed to load planning dashboard');
    } finally {
      setLoading(false);
    }
  }, [fiscalYear]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getUtilizationColor = (percent) => {
    if (percent >= 100) return 'text-red-600 bg-red-50';
    if (percent >= 80) return 'text-amber-600 bg-amber-50';
    return 'text-green-600 bg-green-50';
  };

  const quickActions = [
    { title: 'Budget Management', description: 'Create and manage budgets', icon: Wallet, path: '/ps/planning/budget', color: 'text-orange-600' },
    { title: 'Cost Planning', description: 'Plan costs by WBS', icon: Calculator, path: '/ps/planning/cost-plans', color: 'text-green-600' },
    { title: 'Revenue Planning', description: 'Plan revenues for projects', icon: TrendingUp, path: '/ps/planning/revenue-plans', color: 'text-orange-600' },
    { title: 'Plan vs Actual', description: 'Compare planned vs actual', icon: BarChart3, path: '/ps/planning/comparison', color: 'text-orange-600' },
    { title: 'Commitments', description: 'Track commitments', icon: FileText, path: '/ps/planning/commitments', color: 'text-orange-600' },
    { title: 'Budget Profiles', description: 'Configure budget profiles', icon: Target, path: '/ps/config/budget-profiles', color: 'text-orange-600' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="planning-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planning & Budgeting</h1>
          <p className="text-gray-500">Manage project budgets, costs, and revenue planning</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={fiscalYear.toString()} onValueChange={(v) => setFiscalYear(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(year => (
                <SelectItem key={year} value={year.toString()}>FY {year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => navigate('/ps/planning/budget/new')}>
            + New Budget
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Budget</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(dashboard?.total_budget)}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-full">
                <DollarSign className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Released: {formatCurrency(dashboard?.total_released_budget)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Actual Costs</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(dashboard?.total_actual_costs)}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-full">
                <Activity className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Commitments: {formatCurrency(dashboard?.total_commitments)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Available Budget</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(dashboard?.total_available_budget)}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-full">
                <Wallet className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Planned: {formatCurrency(dashboard?.total_planned_costs)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Budget Utilization</p>
                <p className={`text-2xl font-bold ${dashboard?.overall_budget_utilization_percent >= 100 ? 'text-red-600' : dashboard?.overall_budget_utilization_percent >= 80 ? 'text-amber-600' : 'text-green-600'}`}>
                  {(dashboard?.overall_budget_utilization_percent || 0).toFixed(1)}%
                </p>
              </div>
              <div className={`p-3 rounded-full ${getUtilizationColor(dashboard?.overall_budget_utilization_percent)}`}>
                <PieChart className="h-6 w-6" />
              </div>
            </div>
            <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${dashboard?.overall_budget_utilization_percent >= 100 ? 'bg-red-500' : dashboard?.overall_budget_utilization_percent >= 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(dashboard?.overall_budget_utilization_percent || 0, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-xs text-orange-600 font-medium">Projects with Budget</p>
                <p className="text-xl font-bold text-orange-800">{dashboard?.projects_with_budget || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-xs text-red-600 font-medium">Over Budget</p>
                <p className="text-xl font-bold text-red-800">{dashboard?.projects_over_budget || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-xs text-amber-600 font-medium">Near Limit (&gt;80%)</p>
                <p className="text-xl font-bold text-amber-800">{dashboard?.projects_near_budget_limit || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-xs text-orange-600 font-medium">Pending Releases</p>
                <p className="text-xl font-bold text-orange-800">{dashboard?.pending_budget_releases || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="h-5 w-5" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickActions.map((action) => (
            <Card
              key={action.path || action.title}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(action.path)}
            >
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg bg-gray-50`}>
                    <action.icon className={`h-6 w-6 ${action.color}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{action.title}</h3>
                    <p className="text-sm text-gray-500">{action.description}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Alerts Section */}
      {(dashboard?.projects_over_budget_list?.length > 0 || dashboard?.projects_near_limit_list?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Over Budget Projects */}
          {dashboard?.projects_over_budget_list?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Projects Over Budget
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard.projects_over_budget_list.map((project) => (
                    <div
                      key={project.project_id || project.project_name}
                      className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{project.project_name}</p>
                        <p className="text-sm text-gray-500">Budget: {formatCurrency(project.budget)}</p>
                      </div>
                      <Badge variant="destructive">
                        -{formatCurrency(project.overrun)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Near Limit Projects */}
          {dashboard?.projects_near_limit_list?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-amber-600 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Projects Near Budget Limit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard.projects_near_limit_list.map((project) => (
                    <div
                      key={project.project_id || project.project_name}
                      className="flex items-center justify-between p-3 bg-amber-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{project.project_name}</p>
                        <p className="text-sm text-gray-500">Budget: {formatCurrency(project.budget)}</p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-800">
                        {project.utilization_percent?.toFixed(1)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Recent Budget Documents */}
      {dashboard?.recent_budget_documents?.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Budget Documents</CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/ps/planning/budget')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium text-gray-600">Document</th>
                    <th className="pb-3 font-medium text-gray-600">Project</th>
                    <th className="pb-3 font-medium text-gray-600">Type</th>
                    <th className="pb-3 font-medium text-gray-600">Amount</th>
                    <th className="pb-3 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recent_budget_documents.map((doc) => (
                    <tr key={doc.document_number} className="border-b last:border-0">
                      <td className="py-3 font-medium">{doc.document_number}</td>
                      <td className="py-3 text-gray-600">{doc.project_name || doc.project_id}</td>
                      <td className="py-3">
                        <Badge variant="outline">{doc.budget_type}</Badge>
                      </td>
                      <td className="py-3 font-medium">{formatCurrency(doc.total_amount)}</td>
                      <td className="py-3">
                        <Badge className={
                          doc.release_status === 'RELEASED' ? 'bg-green-100 text-green-800' :
                          doc.release_status === 'PARTIALLY_RELEASED' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {doc.release_status?.replace('_', ' ')}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
    </Layout>
  );
};

export default PlanningDashboard;
